import { Grid, Card, Button, Box, CardHeader } from "@mui/material";
import { FC, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { OdResourcePageProps } from "@/types/resource";
import { Content } from "@/components/content/Content";
import { ClassificationRuleEdit } from "@/components/securefiles/ClassificationRuleEdit";
import CreateOrEdit from "@/controller/CreateOrEdit";
import WithSave from "@/controller/WithSave";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES } from "@/constants";
import { ClassificationRuleInput } from "@/gen/api/securefile";
import { ClassificationRuleAmbinetDataCotnextProvider } from "@/components/securefiles/ClassificationRuleAmbientDataProvider";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import usePermitGuard from "@/authProvider/usePermitGuard";
import { LocalMessageLocation, useLocalMessagingRoot } from "@/contexts/LocalMessagingRoot";
import { useLmStatesPropagate } from "@/contexts/LocalMessageHookProtocols/useLmStatesPropagate";
import { useLmGlobalDisabledMsg } from "@/contexts/LocalMessageHookProtocols/useLmGlobalDisabledMsg";


const CreateClassificationRule: FC<OdResourcePageProps> = (props) => {
  const handlers = useDefaultSavedHandlers("Create File Classification Rule")

  usePermitGuard([[MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES]])
  return (
    <ClassificationRuleAmbinetDataCotnextProvider>
      <CreateOrEdit resource={props.resource} basePath="" {...handlers}>
        <CreateClassificationRuleBody {...props} />
      </CreateOrEdit>
    </ClassificationRuleAmbinetDataCotnextProvider>
  )
}

const CreateClassificationRuleBody: FC<OdResourcePageProps> = ({
  options, resource,
}) => {
  const hasEditPermit = useHasPermit([MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES])
  const dispatch = useDispatch()
    
  const [states, setStates] = useState({
    dirty: false,
  })
    
  const {LocalMessagingRootHost, subscribe, publish} = useLocalMessagingRoot()
  const {assembleSubStates} = useLmStatesPropagate({ 
    key: 'root',
    asUpperStage: {
      dirtyStateInformed: {
        handler() {
          return (_, dirty) => setStates(v => ({...v, dirty }))
        },
        deps: []
      }
    }
  }, {subscribe, publish})
  useLmGlobalDisabledMsg({ authority }, {subscribe, publish})

  const handleSave = (save: (record:any,callbacks?:any)=>void) => async () => {
    if (!hasEditPermit) { return }
    if (!states.dirty) { return }
    
    try {
      const payload: ClassificationRuleInput = (await assembleSubStates(['editor'])).editor
      
      save(payload, {
        onSuccess: () => {
          dispatch(push(`/${resource}`))
        }
      })
    } catch (error) {
      // validation must had failed
    }
  }
  return (
    <Content 
      pageName="Create File Classification Rule" 
      resourceName="Create" 
      breadcrumbs={[...(options?.breadcrumbs ?? []), {
        label: 'File Classification Rules',
        path: `/${resource}`
      }]}
      mainActions={
        <>
          <WithSave 
            render={({save, saving}) =>
              <Button
                color="primary"
                disabled={!states.dirty || saving}
                variant="contained"
                onClick={handleSave(save)}
              >
                Save
              </Button>}
          />
          <Button
            color="primary"
            variant="outlined"
            component={RouterLink}
            to={`/${resource}`}
          >
            Cancel
          </Button>
        </>
      }
    >
      <Grid container spacing={4} sx={{ mt: 2 }} >
        <Grid item xs={12}>
          <Card sx={{ minHeight: 600 }}>
            <CardHeader title="New File Classification Rule" />
            <Box sx={{ mb: 2, px: 4, }}>
              <LocalMessagingRootHost>
                <LocalMessageLocation id="editor">
                  <ClassificationRuleEdit />
                </LocalMessageLocation>
              </LocalMessagingRootHost>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Content>
  )
}

export default CreateClassificationRule;

const authority = () => Promise.resolve(false)
